import { isPlatformBrowser } from '@angular/common';
import { DOCUMENT, inject, Injectable, PLATFORM_ID } from '@angular/core';

import type { IDelayReferences, IScriptTemplateProperties } from './entities/types';

@Injectable({ providedIn: 'root' })
export class ScriptTemplateService {
    private readonly document = inject<Document>(DOCUMENT);
    private readonly platformId = inject(PLATFORM_ID);

    public readonly AUTO_LOAD_DURATION_MS = 5000;
    public readonly INTERACTION_TRIGGER_EVENTS = [
        'keydown',
        'mousemove',
        'wheel',
        'touchmove',
        'touchstart',
        'touchend',
        'click',
    ];

    private readonly delayReferences: IDelayReferences = {};

    public applyScriptTemplate(selector: string, applyProperties: IScriptTemplateProperties = {}) {
        return new Promise((resolve, reject) => {
            const fullSelector = `script[type="script-template"]${selector}`;
            const template = this.document.querySelector(fullSelector);

            if (template) {
                const script = template.cloneNode(true) as HTMLScriptElement;
                Object.keys(applyProperties).forEach((property) => {
                    script.setAttribute(property, applyProperties[property]);
                });
                script.type = 'text/javascript';

                if (isPlatformBrowser(this.platformId)) {
                    script.onload = () => resolve(script);
                    script.onerror = reject;
                }

                template.parentElement.insertBefore(script, template);
                template.remove();
                return isPlatformBrowser(this.platformId) ? script : resolve(script);
            }

            const testSelector = `script[type="text/javascript"]${selector}`;
            const script = this.document.querySelector(testSelector);

            return script ? resolve(script) : reject(`Could not find script template "${fullSelector}"`);
        });
    }

    public delayScriptTemplate(
        selector: string,
        applyProperties: IScriptTemplateProperties = {},
        timeout: number = this.AUTO_LOAD_DURATION_MS
    ): void {
        if (isPlatformBrowser(this.platformId)) {
            const apply = () => this.applyScriptTemplate(selector, applyProperties);
            const trigger = () => this.triggerScriptTemplate(selector);
            const timeoutID = setTimeout(trigger.bind(this), timeout);

            this.delayReferences[selector] = {
                timeoutID,
                trigger: trigger.bind(this),
                apply: apply.bind(this),
            };

            this.INTERACTION_TRIGGER_EVENTS.forEach((event) => {
                this.document.addEventListener(event, this.delayReferences[selector].trigger, {
                    passive: true,
                    once: true,
                });
            });
        }
    }

    public removeScriptTemplate(selector: string): void {
        const fullSelector = `script[type="script-template"]${selector}`;
        const template = this.document.querySelector(fullSelector);

        if (template) {
            template.remove();
        }

        if (isPlatformBrowser(this.platformId) && this.delayReferences[selector]) {
            clearTimeout(this.delayReferences[selector].timeoutID);
            this.INTERACTION_TRIGGER_EVENTS.forEach((event) => {
                this.document.removeEventListener(event, this.delayReferences[selector].trigger);
            });
            delete this.delayReferences[selector];
        }
    }

    private triggerScriptTemplate(selector: string) {
        clearTimeout(this.delayReferences[selector].timeoutID);

        this.INTERACTION_TRIGGER_EVENTS.forEach((event) => {
            this.document.removeEventListener(event, this.delayReferences[selector].trigger);
        });

        this.delayReferences[selector].apply();
    }
}
